<template>
    <div class="EntityDetailsEquipmentSection text-eidDarkBlue">
        <template v-if="!localEquipment || localEquipment.length === 0">
            <div
                data-test-id="EntityDetailsEquipmentSection__noEquipmentText"
                class="EntityDetailsEquipmentSection__emptyContainer"
            >
                <template v-if="editingEnabled">
                    Click "Add Equipment" to add equipment.
                </template>
                <template v-else>
                    Click "Edit Entity Details" to add equipment.
                </template>
            </div>
        </template>
        <template v-else>
            <Table self-contained>
                <template #head>
                    <HeadCell
                        v-for="{ name, label, tooltipData } in columns"
                        :key="name"
                        :data-test-id="`EntityDetailsEquipmentSection__${name}HeadCell`"
                        class="text-eidDarkBlue font-semibold"
                    >
                        <div v-if="label" class="flex justify-between items-center flex-nowrap gap-2">
                            {{ label }}
                            <EidIcon
                                v-if="tooltipData"
                                v-tooltip="tooltipData"
                                class="text-eidQualityBlack-50"
                                alt="icon"
                                :icon-src="faQuestionCircle"
                            />
                        </div>
                    </HeadCell>
                </template>
                <CustomPropertyEquipmentRow
                    v-for="(equipmentItem, index) in localEquipment"
                    :key="rowKeys[index]"
                    :equipment="equipmentItem"
                    :custom-property-equipment="customPropertyEquipment"
                    :updating-insured="updatingEntity"
                    :columns="columns"
                    :rbac-disabled="rbacDisabled"
                    :editing-enabled="editingEnabled"
                    :touched="touched"
                    @input="onEquipmentInput($event, index)"
                    @delete="onDeleteEquipment(index)"
                />
            </Table>
        </template>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { HeadCell, Table } from '@evidentid/dashboard-commons/components/Table';
    import createForm from '@evidentid/json-schema/createForm';
    import {
        JsonSchemaAbstractObject,
        JsonSchemaArray,
    } from '@evidentid/json-schema/interfaces/JsonSchema';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { CustomPropertyEquipment } from '@evidentid/tprm-portal-lib/models/dashboard/CustomProperty.model';

    import CustomPropertyEquipmentRow from './components/CustomPropertyEquipmentRow/CustomPropertyEquipmentRow.vue';
    import { customPropertyEquipmentColumns } from './config';

    export default defineComponent({
        name: 'EntityDetailsEquipmentSection',
        components: {
            EidIcon,
            HeadCell,
            CustomPropertyEquipmentRow,
            Table,
        },
        props: {
            entityDisplayName: {
                type: String as PropType<string>,
                required: true,
            },
            customPropertyEquipment: {
                type: Object as PropType<CustomProperty>,
                required: true,
            },
            equipment: {
                type: Array as PropType<CustomPropertyEquipment[]>,
                default: (): CustomPropertyEquipment[] => [] as CustomPropertyEquipment[],
            },
            updatingEntity: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            rbacDisabled: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            editingEnabled: {
                type: Boolean as PropType<boolean>,
                default: true,
            },
            touched: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'input', 'validity-change' ],
        data() {
            const fieldsDescription = (this.customPropertyEquipment.schema as any).items?.properties ?? {};
            return {
                faQuestionCircle,
                // Copied rather than annotated in place: the column config is shared by every
                // section, and each one's tooltips come from its own property's schema.
                columns: customPropertyEquipmentColumns.map((col) => (
                    col.field
                        ? { ...col, tooltipData: { content: fieldsDescription[col.field]?.description || '' } }
                        : col
                )),
                localEquipment: [] as CustomPropertyEquipment[],
                // Rows carry no identifier, so Vue would otherwise key them by position and move
                // a row's component state onto its neighbour when an earlier row is deleted.
                rowKeys: [] as string[],
                seeded: false,
                nextRowKey: 0,
            };
        },
        computed: {
            equipmentForm(): ReturnType<typeof createForm> {
                const schema =
                    (this.customPropertyEquipment.schema as JsonSchemaArray<JsonSchemaAbstractObject>)
                        .items;
                return createForm(schema);
            },
            allEquipmentValid(): boolean {
                if (!this.localEquipment || this.localEquipment.length === 0) {
                    return true;
                }
                // The schema form is the whole check: an empty currency cell emits null, which it
                // already rejects for a required field, so a row worth nothing is a real value
                // rather than a blank one.
                return this.localEquipment.every(
                    (item) => this.equipmentForm.isValid(this.equipmentForm.getValue(item, true), true),
                );
            },
        },
        watch: {
            equipment: {
                immediate: true,
                handler(newValue?: boolean) {
                    if (!newValue) {
                        return;
                    }
                    // The entity can be replaced while the user is typing — a refresh triggered
                    // from another tab, say. Re-seeding then would throw away their unsaved rows,
                    // so once seeded the local copy belongs to the user until the edit ends.
                    if (this.seeded && this.editingEnabled) {
                        return;
                    }
                    this.localEquipment = [ ...this.equipment ];
                    this.rowKeys = this.localEquipment.map(() => this.takeRowKey());
                    this.seeded = true;
                },
            },
            allEquipmentValid: {
                immediate: true,
                handler(isValid: boolean): void {
                    this.$emit('validity-change', isValid);
                },
            },
        },
        methods: {
            emitInput(): void {
                this.$emit('input', this.localEquipment);
            },
            takeRowKey(): string {
                this.nextRowKey += 1;
                return `equipment-row-${this.nextRowKey}`;
            },
            onClickAddEquipment(): void {
                this.localEquipment = [ ...this.localEquipment, {} as CustomPropertyEquipment ];
                this.rowKeys = [ ...this.rowKeys, this.takeRowKey() ];
                this.emitInput();
            },
            onEquipmentInput(equipmentItem: CustomPropertyEquipment, listIndex: number): void {
                this.localEquipment = [
                    ...this.localEquipment.slice(0, listIndex),
                    equipmentItem,
                    ...this.localEquipment.slice(listIndex + 1),
                ];
                this.emitInput();
            },
            onDeleteEquipment(equipmentIndex: number): void {
                this.localEquipment = [
                    ...this.localEquipment.slice(0, equipmentIndex),
                    ...this.localEquipment.slice(equipmentIndex + 1),
                ];
                this.rowKeys = [
                    ...this.rowKeys.slice(0, equipmentIndex),
                    ...this.rowKeys.slice(equipmentIndex + 1),
                ];
                this.emitInput();
            },
        },
    });
</script>
